/**
 * WithAgentBanner — mounts a SourcingAgentPanel for the given module key
 * into the page's right rail (via RailPortal) when a RailedLayout is
 * active. Falls back to the legacy inline banner when not wrapped (so
 * routes without rails still get an agent panel at the top).
 *
 * iter-143: refactored from inline-only render → portals into right rail.
 * iter-123: key={moduleKey} forces a clean mount on agent swap.
 * iter-124: AGENT_NEIGHBORS + AGENT_LOCATIONS power Cross-Agent Handoff;
 *           AGENT_REGISTRY now lists every active iPCLM Cognara specialist
 *           (Sourcing ss4*, Requisitions rq5*).
 */
import { SourcingAgentPanel } from '@/components/sourcing/SourcingL2Kit';
import { RailPortal, useRails } from '@/components/layout/SourcingRailedLayout';

const AGENT_REGISTRY = {
  // Category & Spend
  scm1: { name: 'Taxonomy Architect Agent',         label: 'SCM-1 · Taxonomy' },
  scm2: { name: 'Spend Cube Storyteller Agent',     label: 'SCM-2 · Spend Analytics' },
  scm3: { name: 'Kraljic Strategist Agent',         label: 'SCM-3 · Kraljic' },
  scm4: { name: 'Category Strategy Drafter Agent',  label: 'SCM-4 · Strategy' },
  scm5: { name: 'Market Intel Scout Agent',         label: 'SCM-5 · Market Intel' },
  scm6: { name: 'Demand Forecaster Agent',          label: 'SCM-6 · Demand' },
  // Supplier Management
  srm1: { name: 'Onboarding Concierge Agent',       label: 'SRM-1 · Onboarding' },
  srm2: { name: 'Qualification Auditor Agent',      label: 'SRM-2 · Qualification' },
  srm3: { name: 'Performance Coach Agent',          label: 'SRM-3 · Performance' },
  srm4: { name: 'Relationship Steward Agent',       label: 'SRM-4 · Relationship' },
  srm5: { name: 'Risk Sentinel Agent',              label: 'SRM-5 · Risk' },
  srm6: { name: 'Data Quality Steward Agent',       label: 'SRM-6 · Data Governance' },
  srm7: { name: 'Diversity Advocate Agent',         label: 'SRM-7 · Diversity' },
  srm8: { name: 'Collaboration Concierge Agent',    label: 'SRM-8 · Collaboration' },
  // Strategic Sourcing
  ss41:  { name: 'Strategy Architect Agent',        label: 'SS-4.1 · Strategy' },
  ss42:  { name: 'Supplier Scout Agent',            label: 'SS-4.2 · Scout' },
  ss43:  { name: 'Clarification Concierge Agent',   label: 'SS-4.3 · Clarification SLA' },
  ss44:  { name: 'Submission Watchdog Agent',       label: 'SS-4.4 · Submission' },
  ss45:  { name: 'Bid Opening Auditor Agent',       label: 'SS-4.5 · Opening' },
  ss46:  { name: 'Evaluation Coach Agent',          label: 'SS-4.6 · Evaluation' },
  ss47:  { name: 'Auction Strategist Agent',        label: 'SS-4.7 · Auction' },
  ss48:  { name: 'BAFO Negotiator Agent',           label: 'SS-4.8 · BAFO' },
  ss49:  { name: 'Framework Steward Agent',         label: 'SS-4.9 · Frameworks' },
  ss410: { name: 'Direct Award Justification Agent',label: 'SS-4.10 · Direct Award' },
  ss411: { name: 'Award Recommendation Narrator Agent',   label: 'SS-4.11 · Award' },
  ss412: { name: 'Award Closure & Comms Agent',     label: 'SS-4.12 · Closure' },
  ss413: { name: 'Communication Concierge Agent',   label: 'SS-4.13 · Comms' },
  ss414: { name: 'Sourcing Analytics Storyteller Agent',  label: 'SS-4.14 · Analytics · FX' },
  ss415: { name: 'Supplier Portal Steward Agent',   label: 'SS-4.15 · Portal' },
  ss416: { name: 'ITT Compliance Sentinel Agent',   label: 'SS-4.16 · ITT Compliance' },
  // Requisitions
  rq5:   { name: 'Requisitions Concierge Agent',    label: 'RQ-5 · Concierge' },
  rq51:  { name: 'P2P Dashboard Curator Agent',     label: 'RQ-5.1 · P2P Dashboard' },
  rq52:  { name: 'Conversational PR Co-Pilot Agent',label: 'RQ-5.2 · PR Co-Pilot' },
  rq53:  { name: 'NL Intake Parser Agent',          label: 'RQ-5.3 · NL Intake' },
  rq54:  { name: 'Enrichment Forensic Agent',       label: 'RQ-5.4 · Enrichment' },
  rq55:  { name: 'Policy Compliance Sentinel Agent',      label: 'RQ-5.5 · Budget · Policy' },
  rq56:  { name: 'Approval Matrix Architect Agent',       label: 'RQ-5.6 · Matrix' },
  rq57:  { name: 'Approval Workflow Conductor Agent',     label: 'RQ-5.7 · Approval Chain' },
  rq58:  { name: 'Approver Experience Concierge Agent',   label: 'RQ-5.8 · Approver UX' },
  // Purchase Orders
  po61: { name: 'Workbench Triage Agent',                 label: 'PO-6.1 · Workbench' },
  po62: { name: 'PO Creation Co-Pilot Agent',             label: 'PO-6.2 · Creation' },
  po63: { name: 'Approval & Lifecycle Steward Agent',     label: 'PO-6.3 · Approvals' },
  po64: { name: 'Change Order Impact Analyst Agent',      label: 'PO-6.6 · Change Orders' },
  po65: { name: '3-Way Match Detective Agent',            label: 'PO-6.7 · 3-Way Match' },
  po66: { name: 'Acknowledgement & Dispatch Concierge Agent',   label: 'PO-6.4 · Ack & Dispatch' },
  po67: { name: 'Maverick Spend Hunter Agent',            label: 'PO-6.8.1 · Maverick' },
  po68: { name: 'Cycle-Time & Supplier Ack Analyst Agent',      label: 'PO-6.8.2/3 · Analytics' },
  // Goods Receipt — 13 agents (7 sub-module + 6 sub-tab specialists)
  gr71:  { name: 'Inbound Planner Agent',                  label: 'GR-7.1 · Inbound' },
  gr711: { name: 'ASN & EDI Intake Specialist Agent',      label: 'GR-7.1.2 · ASN Intake' },
  gr712: { name: 'Dock Slot Optimizer Agent',              label: 'GR-7.1.3 · Dock Planning' },
  gr72:  { name: 'Receipt Capture Co-Pilot Agent',         label: 'GR-7.2 · Capture' },
  gr722: { name: 'Mobile Receiving Coach Agent',           label: 'GR-7.2.2 · Mobile PWA' },
  gr73:  { name: 'Quality Inspection Coach Agent',         label: 'GR-7.3 · Inspection' },
  gr731: { name: 'Sampling Plan & AQL Specialist Agent',   label: 'GR-7.3.1 · Sampling' },
  gr74:  { name: 'Returns & Claims Resolver Agent',        label: 'GR-7.4 · RMAs · Claims' },
  gr741: { name: 'Returns Logistics Coordinator Agent',    label: 'GR-7.4.1 · Returns Logistics' },
  gr75:  { name: 'Inventory & GL Posting Agent',           label: 'GR-7.5 · GL' },
  gr76:  { name: 'Service Entry Sheet Steward Agent',      label: 'GR-7.6 · SES' },
  gr77:  { name: 'Receipt Anomaly Hunter Agent',           label: 'GR-7.7 · Analytics' },
  gr771: { name: 'OTIF Performance Coach Agent',           label: 'GR-7.7.1 · OTIF' },
  // Contracts & Claims
  contracts: { name: 'Contract Lifecycle Steward Agent',  label: 'CON-7 · Contracts' },
  claims:    { name: 'Claims & LD Resolver Agent',        label: 'CL-8 · Claims & LD' },
  // Supplier 360° (iter-112)
  sup73:     { name: 'Supplier 360° Co-Pilot Agent',      label: 'SRM-3 · Supplier 360°' },
  // iter-113 — Invoices module agent
  invoices:  { name: 'AP Invoice Auto-Match Coach Agent', label: 'INV-7 · Invoices & Pay' },
  // iter-227 — Invoices per-tab specialists (replaces the "Cognara / Idle"
  // placeholder that used to render on every invoice sub-tab).
  'invoice-l4-dashboard': { name: 'AP Dashboard Curator Agent',        label: 'INV-7.1 · Dashboard' },
  'invoice-l4-intake':    { name: 'Invoice Intake & OCR Coach Agent',   label: 'INV-7.2 · Intake · OCR' },
  'invoice-l4-list':      { name: 'AP Invoice Auto-Match Coach Agent',  label: 'INV-7.3 · All Invoices' },
  'invoice-l4-keyer':     { name: 'Keyer Queue Concierge Agent',        label: 'INV-7.4 · Keyer Queue' },
  'invoice-l4-match':     { name: '3-Way Match Detective Agent',        label: 'INV-7.5 · Match' },
  'invoice-l4-approvals': { name: 'Invoice Approval Steward Agent',     label: 'INV-7.6 · Approvals' },
  'invoice-l4-payruns':   { name: 'Pay Run & Cash-flow Planner Agent',  label: 'INV-7.7 · Pay Runs' },
  'invoice-l4-analytics': { name: 'AP Analytics Storyteller Agent',     label: 'INV-7.8 · Analytics' },
  'invoice-l4-fraud':     { name: 'Fraud & Anomaly Hunter Agent',       label: 'INV-7.9 · Fraud' },
};

/**
 * AGENT_NEIGHBORS — closest-cousin specialist for one-click handoff.
 * Each entry lists 2-4 related agents that share scope or hand off
 * naturally (e.g. WMS placement → GL posting → Anomaly hunter).
 */
const AGENT_NEIGHBORS = {
  // GR neighbors
  gr71:  ['gr711', 'gr712', 'gr72'],
  gr711: ['gr71',  'gr712', 'gr72'],
  gr712: ['gr711', 'gr71'],
  gr72:  ['gr722', 'gr73', 'gr75'],
  gr722: ['gr72',  'gr73'],
  gr73:  ['gr731', 'gr74', 'gr75'],
  gr731: ['gr73',  'gr74'],
  gr74:  ['gr741', 'gr73'],
  gr741: ['gr74',  'gr75'],
  gr75:  ['gr72',  'gr77', 'invoices'],
  gr76:  ['gr75',  'po66'],
  gr77:  ['gr771', 'gr75', 'po68'],
  gr771: ['gr77',  'srm3'],
  // PO neighbors
  po61: ['po62', 'po63', 'po67'],
  po62: ['po61', 'po63', 'po64'],
  po63: ['po64', 'po66', 'po65'],
  po64: ['po63', 'po65', 'po66'],
  po65: ['gr75', 'invoices', 'po64'],
  po66: ['po63', 'po64', 'ss413'],
  po67: ['po61', 'po63', 'rq55'],
  po68: ['po67', 'gr771', 'rq51'],
  // SRM neighbors
  srm1: ['srm2', 'srm6'],
  srm2: ['srm1', 'srm5', 'srm3'],
  srm3: ['srm2', 'srm5', 'sup73'],
  srm4: ['srm3', 'srm8'],
  srm5: ['srm2', 'srm3', 'srm6'],
  srm6: ['srm1', 'srm5'],
  srm7: ['srm5', 'srm3'],
  srm8: ['srm4', 'ss415'],
  // Sourcing neighbors
  ss41:  ['ss42', 'ss43'],
  ss42:  ['ss41', 'ss44', 'srm2'],
  ss43:  ['ss42', 'ss44', 'ss45'],
  ss44:  ['ss43', 'ss45'],
  ss45:  ['ss46', 'ss411'],
  ss46:  ['ss45', 'ss47', 'ss48'],
  ss47:  ['ss48', 'ss46'],
  ss48:  ['ss47', 'ss411'],
  ss49:  ['rq51', 'rq55', 'po62'],
  ss410: ['ss411', 'ss416'],
  ss411: ['ss412', 'contracts'],
  ss412: ['ss411', 'contracts'],
  ss413: ['po66'],
  ss414: ['ss411', 'rq51'],
  ss415: ['srm8'],
  ss416: ['ss410', 'ss411'],
  // Requisitions neighbors
  rq5:  ['rq51', 'rq52'],
  rq51: ['rq57', 'rq58', 'po61'],
  rq52: ['rq53', 'rq54', 'rq5'],
  rq53: ['rq52', 'rq54'],
  rq54: ['rq53', 'rq55', 'invoices'],
  rq55: ['rq56', 'rq57'],
  rq56: ['rq57', 'rq58'],
  rq57: ['rq58', 'rq51'],
  rq58: ['rq57', 'rq51'],
  // Cross-module
  contracts: ['ss411', 'po63'],
  invoices:  ['po65', 'gr75'],
  sup73:     ['srm3', 'srm5', 'gr771'],
};

/**
 * AGENT_LOCATIONS — where to navigate when handing off to a given agent.
 * If absent, the handoff just dispatches an event without route change.
 */
const AGENT_LOCATIONS = {
  // GR
  gr71:  '/app/grn?tab=inbound',
  gr711: '/app/grn?tab=l4-closure&sub=email',
  gr712: '/app/grn?tab=asn',
  gr72:  '/app/grn?tab=capture',
  gr722: '/app/grn?tab=mobile',
  gr73:  '/app/grn?tab=detail',
  gr731: '/app/grn?tab=detail&sub=inspection',
  gr74:  '/app/grn?tab=returns',
  gr741: '/app/grn?tab=returns&sub=logistics',
  gr75:  '/app/grn?tab=l4-closure&sub=wms',
  gr76:  '/app/grn?tab=ses',
  gr77:  '/app/grn?tab=l4-closure&sub=sched',
  gr771: '/app/grn?tab=analytics',
  // PO
  po61: '/app/po?tab=workbench',
  po62: '/app/po?tab=create',
  po63: '/app/po?tab=detail',
  po64: '/app/po?tab=detail&sub=changes',
  po65: '/app/po?tab=match',
  po66: '/app/po?tab=l4-closure&sub=counter',
  po67: '/app/po?tab=analytics',
  po68: '/app/po?tab=analytics',
  // SRM
  srm1: '/app/srm/registration',
  srm2: '/app/srm/qualification',
  srm3: '/app/srm/performance',
  srm4: '/app/srm/collaboration',
  srm5: '/app/srm/extras?tab=risk',
  srm6: '/app/srm/master',
  srm7: '/app/srm/extras?tab=avl',
  srm8: '/app/srm/collaboration',
  // Sourcing
  ss43:  '/app/sourcing/extras?tab=sla',
  ss47:  '/app/sourcing/extras?tab=auction',
  ss48:  '/app/sourcing/extras?tab=bafo',
  ss49:  '/app/sourcing/extras?tab=calloff',
  ss411: '/app/sourcing/extras?tab=award',
  ss414: '/app/sourcing/extras?tab=fx',
  ss415: '/app/srm/collaboration',
  // Requisitions
  rq51: '/app/requisitions?tab=dashboard',
  rq52: '/app/requisitions?tab=conversational',
  rq54: '/app/requisitions?tab=l4-closure&sub=punchout',
  rq55: '/app/requisitions?tab=l4-closure&sub=budget',
  rq57: '/app/requisitions?tab=l4-closure&sub=approval',
  rq58: '/app/requisitions?tab=l4-closure&sub=recall',
  // Cross-module
  contracts: '/app/cm/repository',
  invoices:  '/app/invoices',
  sup73:     '/app/srm/performance',
};

export default function WithAgentBanner({ moduleKey, contextData, children }) {
  const a = AGENT_REGISTRY[moduleKey];
  const rails = useRails();
  if (!a) return <>{children}</>;
  // Resolve handoff neighbors (rich objects with name + label + location)
  const neighborKeys = AGENT_NEIGHBORS[moduleKey] || [];
  const neighbors = neighborKeys
    .map((k) => AGENT_REGISTRY[k] && ({
      key: k, name: AGENT_REGISTRY[k].name, label: AGENT_REGISTRY[k].label,
      location: AGENT_LOCATIONS[k] || null,
    }))
    .filter(Boolean);
  // key={moduleKey} forces a fresh mount of the chat panel whenever the
  // active agent swaps (e.g. moving between L4 sub-tabs). This resets the
  // conversation, sessionId, and starters to the new specialist — without
  // bleeding state across personas.
  // When mounted inside a rails context the panel is the entire rail
  // surface — open it by default so the chat / starters / voice controls
  // fill the rail instead of leaving 90% whitespace below a collapsed header.
  const agent = (
    <SourcingAgentPanel
      key={moduleKey}
      moduleKey={moduleKey}
      agentName={a.name}
      contextData={{ stage: a.label, ...(contextData || {}) }}
      neighbors={neighbors}
      defaultOpen={!!rails}
    />
  );
  if (rails) {
    return (
      <>
        <RailPortal side="right">{agent}</RailPortal>
        {children}
      </>
    );
  }
  // Legacy fallback: render inline at top of the page.
  return (
    <>
      <div className="w-full px-2 pt-2" data-testid={`page-agent-banner-${moduleKey}`}>{agent}</div>
      {children}
    </>
  );
}

export { AGENT_REGISTRY, AGENT_NEIGHBORS, AGENT_LOCATIONS };
